import { useState } from 'react';
import Reveal from '@/components/feature/Reveal';
import SectionHeading from './SectionHeading';
import { formatChecklist, formatModules } from '@/mocks/guide';

const toneStyles: Record<string, { icon: string; badge: string }> = {
  primary: { icon: 'bg-primary-100 text-primary-700', badge: 'bg-primary-100 text-primary-800' },
  secondary: { icon: 'bg-secondary-100 text-secondary-700', badge: 'bg-secondary-100 text-secondary-800' },
  accent: { icon: 'bg-accent-100 text-accent-700', badge: 'bg-accent-100 text-accent-800' },
};

export default function FormatGuide() {
  const [activeKey, setActiveKey] = useState(formatModules[0].key);
  const [checked, setChecked] = useState<number[]>([]);

  const activeModule = formatModules.find((item) => item.key === activeKey) ?? formatModules[0];
  const activeTone = toneStyles[activeModule.tone] ?? toneStyles.primary;

  const toggleCheck = (index: number) => {
    setChecked((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  return (
    <section id="format" className="w-full bg-background-100 py-16 md:py-24 border-y border-background-200">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          kicker="FORMAT GUIDE"
          title="投稿格式要求：四类材料分别怎么交"
          desc="不同形式的材料有不同的规范。选下面任意一类查看具体要求，投稿前照着核对一遍，能避免绝大多数「材料不清、无法核实」的退稿。"
        />

        <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5 md:gap-6">
          <Reveal>
            <div className="p-1.5 rounded-lg bg-background-50 border border-background-200">
              <div
                role="tablist"
                aria-label="投稿格式分类"
                className="flex lg:flex-col gap-1 overflow-x-auto"
              >
                {formatModules.map((item) => {
                  const isActive = item.key === activeKey;
                  const tone = toneStyles[item.tone] ?? toneStyles.primary;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveKey(item.key)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-left whitespace-nowrap cursor-pointer transition-colors ${
                        isActive ? 'bg-foreground-950 text-background-50' : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <span
                        className={`w-8 h-8 flex items-center justify-center rounded-md ${
                          isActive ? 'bg-background-50/15 text-background-50' : tone.icon
                        }`}
                      >
                        <i className={`${item.icon} text-lg`} aria-hidden="true" />
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium">{item.title}</span>
                      </span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${isActive ? 'bg-background-50/15 text-background-100' : tone.badge}`}>
                        {item.required}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="h-full p-6 md:p-8 rounded-lg bg-background-50 border border-background-200">
              <div className="flex items-center gap-3">
                <span className={`w-12 h-12 flex items-center justify-center rounded-md ${activeTone.icon}`}>
                  <i className={`${activeModule.icon} text-2xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading font-semibold text-xl text-foreground-950">{activeModule.title}</h3>
                  <p className="text-xs text-foreground-500">要求等级：{activeModule.required}</p>
                </div>
              </div>
              <ul className="mt-6 space-y-4">
                {activeModule.points.map((point) => (
                  <li key={point} className="flex gap-3 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                    <i className="ri-check-line mt-1 text-primary-600 shrink-0" aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={60}>
          <div className="mt-6 md:mt-8 p-6 md:p-8 rounded-lg bg-secondary-950">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h3 className="font-heading font-semibold text-lg md:text-xl text-background-50">投稿前自查清单</h3>
                <p className="mt-1 text-sm text-background-300">逐项确认后再提交，已勾选 {checked.length} / {formatChecklist.length} 项。</p>
              </div>
              <div className="h-1.5 w-full md:w-56 rounded-full bg-background-50/15 overflow-hidden">
                <div
                  className="h-full bg-primary-400 transition-all duration-300"
                  style={{ width: `${(checked.length / formatChecklist.length) * 100}%` }}
                ></div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {formatChecklist.map((item, index) => {
                const isChecked = checked.includes(index);
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleCheck(index)}
                    aria-pressed={isChecked}
                    className={`flex items-start gap-3 p-3.5 rounded-md text-left text-sm cursor-pointer transition-colors ${
                      isChecked ? 'bg-background-50/10 text-background-50' : 'bg-background-50/5 text-background-200 hover:bg-background-50/10'
                    }`}
                  >
                    <span
                      className={`mt-0.5 w-5 h-5 flex items-center justify-center rounded border shrink-0 ${
                        isChecked ? 'bg-primary-500 border-primary-500 text-background-50' : 'border-background-50/40'
                      }`}
                    >
                      {isChecked && <i className="ri-check-line text-xs" aria-hidden="true" />}
                    </span>
                    <span className={isChecked ? 'line-through decoration-background-50/40' : ''}>{item}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}